import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import { DashboardContentContainer } from '@/components/layout/DashboardContentContainer';
import {
  ArrowLeft,
  TrendingUp,
  Users,
  PoundSterling,
  Utensils,
  Calendar,
  CreditCard,
  CheckCircle2,
  Clock,
  UserPlus,
  Gift,
  AlertCircle,
  BarChart3,
  PieChart as PieChartIcon,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { Event, RSVP, Invoice } from '@/types';
import { formatCurrency } from '@/lib/utils';
import { mapEventData, mapRsvpData, isActiveInvoiceData, countAttendeeRsvps } from '@/lib/event-utils';
import { resolveGuestMealChoice } from '@/lib/place-card-helpers';
import { EventHeaderCredits } from '@/components/event/EventHeaderCredits';
import { isMultiTenantEvent } from '@/lib/ticket-mode';

// Masonic color palette
const COLORS = {
  navy: '#1E3A5F',
  gold: '#D4AF37',
  green: '#10B981',
  blue: '#3B82F6',
  purple: '#8B5CF6',
  orange: '#F59E0B',
  red: '#EF4444',
  teal: '#14B8A6',
  amber: '#F59E0B',
};

const CHART_COLORS = [
  COLORS.navy,
  COLORS.gold,
  COLORS.green,
  COLORS.blue,
  COLORS.purple,
  COLORS.orange,
  COLORS.teal,
  COLORS.amber,
];

interface AnalyticsData {
  // Financial
  totalRevenue: number;
  totalOutstanding: number;
  expectedTotal: number;
  collectionRate: number;
  totalPaid: number;
  totalAttendees: number;
  paymentRate: number;
  averageTicketPrice: number;
  processingFeesTotal: number;
  invoiceStatusBreakdown: Array<{ name: string; value: number }>;
  paymentMethodBreakdown: Array<{ name: string; value: number; amount: number }>;
  // Attendance
  attendanceByRank: Array<{ rank: string; count: number }>;
  attendanceByStatus: Array<{ status: string; count: number }>;
  capacityUtilization: number | null;
  // Engagement
  checkedInCount: number;
  checkInRate: number;
  apologiesCount: number;
  invitedCount: number;
  inviterPaysCount: number;
  onTimeRsvpCount: number;
  lateRsvpCount: number;
  rsvpOnTimeRate: number;
  // Meal & Dietary
  mealBreakdown: Array<{ name: string; value: number }>;
  dietaryBreakdown: Array<{ name: string; value: number }>;
  mealCompletionRate: number;
  allergiesCount: number;
  specialRequestsCount: number;
  // Ticket-based (when event uses tickets)
  attendanceByTicketType: Array<{ ticketName: string; count: number; revenue: number }>;
  // Timeline
  paymentTimeline: Array<{ date: string; amount: number; count: number }>;
  rsvpTimeline: Array<{ date: string; count: number }>;
}

export default function AnalyticsDashboardPage() {
  const { unitId, eventId } = useParams<{ unitId: string; eventId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<Event | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!unitId || !eventId) {
        setLoading(false);
        return;
      }

      try {
        const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
        if (eventDoc.exists()) setEvent(mapEventData(eventDoc, unitId));

        const rsvpsSnapshot = await getDocs(
          collection(db, 'units', unitId, 'events', eventId, 'rsvps')
        );
        const rsvps: RSVP[] = rsvpsSnapshot.docs
          .map((d) => mapRsvpData(d as any))
          .filter((r): r is RSVP => r !== null)
          .map((r) => {
            r.eventId = eventId;
            return r;
          });

        const invoicesSnapshot = await getDocs(
          collection(db, 'units', unitId, 'events', eventId, 'invoices')
        );
        const invoices: Invoice[] = [];
        invoicesSnapshot.forEach((invoiceDoc) => {
          const data = invoiceDoc.data();
          if (!isActiveInvoiceData(data)) {
            return;
          }
          invoices.push({
            id: invoiceDoc.id,
            eventId,
            unitId,
            ownerUid: data.ownerUid,
            totalAmount: data.totalAmount,
            amountPaid: data.amountPaid,
            outstandingBalance: data.outstandingBalance,
            status: data.status,
            rsvps: data.rsvps || [],
            paymentHistory: data.paymentHistory || [],
            paymentLink: data.paymentLink,
            remindersSent: data.remindersSent || [],
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          });
        });

        const totalRevenue = invoices.reduce((sum, inv) => sum + inv.amountPaid, 0);
        const totalOutstanding = invoices.reduce((sum, inv) => sum + inv.outstandingBalance, 0);
        const expectedTotal = invoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
        const totalAttendees = countAttendeeRsvps(rsvps);
        const totalPaid = rsvps.filter((r) => r.paid).length;
        const processingFeesTotal = invoices.reduce((sum, inv) => {
          return sum + (inv.paymentHistory || []).reduce((s, p) => s + (p.processingFee || 0), 0);
        }, 0);

        const rsvpDeadline = eventDoc.exists() ? (eventDoc.data()?.rsvpDeadline?.toDate?.() || null) : null;

        // Invoice status breakdown
        const invoiceStatusMap = new Map<string, number>();
        invoices.forEach((inv) => {
          const label = inv.status.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
          invoiceStatusMap.set(label, (invoiceStatusMap.get(label) || 0) + 1);
        });
        const invoiceStatusBreakdown = Array.from(invoiceStatusMap.entries()).map(([name, value]) => ({
          name,
          value,
        }));

        // Payment method breakdown
        const paymentMethodMap = new Map<string, { count: number; amount: number }>();
        invoices.forEach((inv) => {
          (inv.paymentHistory || []).forEach((p) => {
            const method = p.method || 'unknown';
            const label = method === 'stripe' || method === 'card' ? 'Card (Stripe)' : method === 'sumup' ? 'Card (SumUp)' : method === 'slickpay' ? 'SlickPay' : method === 'bank_transfer' ? 'Bank Transfer' : method;
            const current = paymentMethodMap.get(label) || { count: 0, amount: 0 };
            paymentMethodMap.set(label, {
              count: current.count + 1,
              amount: current.amount + p.amount,
            });
          });
        });
        const paymentMethodBreakdown = Array.from(paymentMethodMap.entries()).map(([name, data]) => ({
          name,
          value: data.count,
          amount: data.amount,
        }));

        // Meal breakdown (use resolveGuestMealChoice for ticket-based events)
        const mealCounts = new Map<string, number>();
        rsvps.forEach((rsvp) => {
          const meal = resolveGuestMealChoice(rsvp, eventDoc.exists() ? { tickets: eventDoc.data()?.tickets, courses: eventDoc.data()?.courses } : null) || 'Not specified';
          mealCounts.set(meal, (mealCounts.get(meal) || 0) + 1);
        });
        const mealBreakdown = Array.from(mealCounts.entries()).map(([name, value]) => ({
          name,
          value,
        }));

        // Dietary breakdown
        const dietaryCounts = new Map<string, number>();
        rsvps.forEach((rsvp) => {
          if (rsvp.responses?.Dietary && Array.isArray(rsvp.responses.Dietary)) {
            rsvp.responses.Dietary.forEach((diet: string) => {
              dietaryCounts.set(diet, (dietaryCounts.get(diet) || 0) + 1);
            });
          }
        });
        const dietaryBreakdown = Array.from(dietaryCounts.entries()).map(([name, value]) => ({
          name,
          value,
        }));

        // Meal completion rate
        const withMeal = rsvps.filter((r) => {
          const m = resolveGuestMealChoice(r, eventDoc.exists() ? { tickets: eventDoc.data()?.tickets, courses: eventDoc.data()?.courses } : null);
          return m && m !== 'Not specified';
        }).length;
        const mealCompletionRate = totalAttendees > 0 ? (withMeal / totalAttendees) * 100 : 0;

        const allergiesCount = rsvps.filter((r) => r.responses?.Allergies && String(r.responses.Allergies).trim()).length;
        const specialRequestsCount = rsvps.filter((r) => r.specialRequests && String(r.specialRequests).trim()).length;

        // Payment timeline
        const paymentsByDate = new Map<string, { amount: number; count: number }>();
        invoices.forEach((invoice) => {
          invoice.paymentHistory?.forEach((payment) => {
            const paymentDate = payment.date instanceof Date ? payment.date : new Date(payment.date);
            const dateStr = paymentDate.toLocaleDateString('en-GB');
            const existing = paymentsByDate.get(dateStr) || { amount: 0, count: 0 };
            paymentsByDate.set(dateStr, {
              amount: existing.amount + payment.amount,
              count: existing.count + 1,
            });
          });
        });
        const paymentTimeline = Array.from(paymentsByDate.entries())
          .map(([date, data]) => ({ date, ...data }))
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        // RSVP timeline
        const rsvpsByDate = new Map<string, number>();
        rsvps.forEach((rsvp) => {
          const d = rsvp.createdAt instanceof Date ? rsvp.createdAt : new Date(rsvp.createdAt);
          const dateStr = d.toLocaleDateString('en-GB');
          rsvpsByDate.set(dateStr, (rsvpsByDate.get(dateStr) || 0) + 1);
        });
        const rsvpTimeline = Array.from(rsvpsByDate.entries())
          .map(([date, count]) => ({ date, count }))
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        // Attendance by rank
        const rankCounts = new Map<string, number>();
        rsvps.forEach((rsvp) => {
          const r = rsvp.rank || 'Unknown';
          rankCounts.set(r, (rankCounts.get(r) || 0) + 1);
        });
        const attendanceByRank = Array.from(rankCounts.entries())
          .map(([rank, count]) => ({ rank, count }))
          .sort((a, b) => b.count - a.count);

        // Attendance by status
        const statusCounts = new Map<string, number>();
        rsvps.forEach((rsvp) => {
          const s = rsvp.status || 'unknown';
          const label = s.charAt(0).toUpperCase() + s.slice(1);
          statusCounts.set(label, (statusCounts.get(label) || 0) + 1);
        });
        const attendanceByStatus = Array.from(statusCounts.entries()).map(([status, count]) => ({
          status,
          count,
        }));

        const eventData = eventDoc.data();
        const maxCapacity = eventData?.maxCapacity;
        const capacityUtilization = maxCapacity && totalAttendees > 0 ? (totalAttendees / maxCapacity) * 100 : null;

        const checkedInCount = rsvps.filter((r) => r.checkedIn).length;
        const checkInRate = totalAttendees > 0 ? (checkedInCount / totalAttendees) * 100 : 0;
        const apologiesCount = rsvps.filter((r) => r.apologies).length;
        const invitedCount = rsvps.filter((r) => r.invitedBy).length;
        const inviterPaysCount = rsvps.filter(
          (r) => !!r.invitedBy && r.inviterPaysFor !== false,
        ).length;

        let onTimeRsvpCount = 0;
        let lateRsvpCount = 0;
        if (rsvpDeadline) {
          const deadline = rsvpDeadline instanceof Date ? rsvpDeadline : new Date(rsvpDeadline);
          rsvps.forEach((rsvp) => {
            const created = rsvp.createdAt instanceof Date ? rsvp.createdAt : new Date(rsvp.createdAt);
            if (created <= deadline) onTimeRsvpCount++;
            else lateRsvpCount++;
          });
        }
        const rsvpOnTimeRate = rsvps.length > 0 && (onTimeRsvpCount + lateRsvpCount) > 0
          ? (onTimeRsvpCount / (onTimeRsvpCount + lateRsvpCount)) * 100
          : 100;

        // Attendance by ticket type (when event uses tickets)
        const ticketMap = new Map<string, { count: number; revenue: number }>();
        const tickets = eventData?.tickets || [];
        rsvps.forEach((rsvp) => {
          const ticketEntries = Array.isArray(rsvp.responses?.Tickets) ? rsvp.responses.Tickets as any[] : [];
          const inv = invoices.find((i) => i.rsvps?.includes(rsvp.id));
          const rsvpRevenue = inv ? inv.amountPaid / (inv.rsvps?.length || 1) : 0;
          if (ticketEntries.length > 0) {
            ticketEntries.forEach((te: any) => {
              if (te?.qty > 0) {
                const t = tickets.find((tk: any) => tk.id === te.id);
                const name = t?.name || te.id || 'Unknown';
                const cur = ticketMap.get(name) || { count: 0, revenue: 0 };
                ticketMap.set(name, {
                  count: cur.count + (te.qty || 1),
                  revenue: cur.revenue + rsvpRevenue * (te.qty || 1) / (ticketEntries.reduce((s: number, x: any) => s + (x.qty || 0), 0) || 1),
                });
              }
            });
          } else if (tickets.length === 1) {
            const name = tickets[0].name || 'Standard';
            const cur = ticketMap.get(name) || { count: 0, revenue: 0 };
            ticketMap.set(name, { count: cur.count + 1, revenue: cur.revenue + rsvpRevenue });
          }
        });
        const attendanceByTicketType = Array.from(ticketMap.entries()).map(([ticketName, data]) => ({
          ticketName,
          count: data.count,
          revenue: data.revenue,
        }));

        setAnalytics({
          totalRevenue,
          totalOutstanding,
          expectedTotal,
          collectionRate: expectedTotal > 0 ? (totalRevenue / expectedTotal) * 100 : 0,
          totalPaid,
          totalAttendees,
          paymentRate: totalAttendees > 0 ? (totalPaid / totalAttendees) * 100 : 0,
          averageTicketPrice: totalAttendees > 0 ? totalRevenue / totalAttendees : 0,
          processingFeesTotal,
          invoiceStatusBreakdown,
          paymentMethodBreakdown,
          attendanceByRank,
          attendanceByStatus,
          capacityUtilization,
          checkedInCount,
          checkInRate,
          apologiesCount,
          invitedCount,
          inviterPaysCount,
          onTimeRsvpCount,
          lateRsvpCount,
          rsvpOnTimeRate,
          mealBreakdown,
          dietaryBreakdown,
          mealCompletionRate,
          allergiesCount,
          specialRequestsCount,
          attendanceByTicketType,
          paymentTimeline,
          rsvpTimeline,
        });
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [unitId, eventId]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!event || !analytics) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <p className="text-muted-foreground">No analytics data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 w-full min-w-0 bg-gray-50 py-6 sm:py-8">
        <DashboardContentContainer>
          <div className="mb-6 sm:mb-8 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate(`/unit/${unitId}/event/${eventId}`)}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Event
            </Button>
            <EventHeaderCredits unitId={unitId} />
          </div>
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl font-bold sm:text-3xl">Event Analytics</h1>
            <p className="text-muted-foreground break-words">{event.name}</p>
          </div>

          {/* Financial KPIs */}
          <div className="mb-8">
            <h2 className="mb-4 flex flex-wrap items-center gap-2 text-lg font-semibold">
              <PoundSterling className="h-5 w-5 shrink-0" />
              Financial Overview
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4 xl:grid-cols-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                  <PoundSterling className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    {formatCurrency(analytics.totalRevenue)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatCurrency(analytics.totalOutstanding)} outstanding
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Expected Total</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(analytics.expectedTotal)}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {analytics.collectionRate.toFixed(1)}% collected
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Payment Rate</CardTitle>
                  <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.paymentRate.toFixed(1)}%</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {analytics.totalPaid} of {analytics.totalAttendees} paid
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Ticket Price</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatCurrency(analytics.averageTicketPrice)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {event.costPerPerson ? formatCurrency(event.costPerPerson) : 'N/A'} standard
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Card processing fees</CardTitle>
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(analytics.processingFeesTotal)}</div>
                  <p className="text-xs text-muted-foreground mt-1">Platform fees collected</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Invoice Status</CardTitle>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-sm font-medium">
                    {analytics.invoiceStatusBreakdown.map((s) => `${s.value} ${s.name}`).join(', ') || 'N/A'}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Attendance KPIs */}
          <div className="mb-8">
            <h2 className="mb-4 flex flex-wrap items-center gap-2 text-lg font-semibold">
              <Users className="h-5 w-5 shrink-0" />
              Attendance Overview
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4 xl:grid-cols-7">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Attendees</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.totalAttendees}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {event.maxCapacity ? `${event.maxCapacity} capacity` : 'No limit'}
                    {analytics.capacityUtilization != null && ` (${analytics.capacityUtilization.toFixed(0)}% full)`}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Checked In</CardTitle>
                  <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.checkedInCount}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {analytics.checkInRate.toFixed(1)}% check-in rate
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Apologies</CardTitle>
                  <AlertCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.apologiesCount}</div>
                  <p className="text-xs text-muted-foreground mt-1">Sent apologies</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Invited</CardTitle>
                  <UserPlus className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.invitedCount}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {analytics.inviterPaysCount} inviter-pays
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">RSVP On Time</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.rsvpOnTimeRate.toFixed(0)}%</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {analytics.onTimeRsvpCount} on time, {analytics.lateRsvpCount} late
                  </p>
                </CardContent>
              </Card>
              {!isMultiTenantEvent(event) && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Meal Specified</CardTitle>
                    <Utensils className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{analytics.mealCompletionRate.toFixed(0)}%</div>
                    <p className="text-xs text-muted-foreground mt-1">With meal preference</p>
                  </CardContent>
                </Card>
              )}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Special Notes</CardTitle>
                  <Gift className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {analytics.allergiesCount + analytics.specialRequestsCount}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {analytics.allergiesCount} allergies, {analytics.specialRequestsCount} requests
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Charts Row 1: Meal, Rank, Status */}
          <div className="mb-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {isMultiTenantEvent(event) ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Utensils className="h-5 w-5" />
                    Ticket Quantities
                  </CardTitle>
                  <CardDescription>Distribution of tickets purchased</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={analytics.attendanceByTicketType.map((t) => ({ name: t.ticketName, value: t.count }))}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) =>
                          `${name} (${(percent * 100).toFixed(0)}%)`
                        }
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {analytics.attendanceByTicketType.map((_entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Utensils className="h-5 w-5" />
                    Meal Preferences
                  </CardTitle>
                  <CardDescription>Distribution of meal choices</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={analytics.mealBreakdown}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) =>
                          `${name} (${(percent * 100).toFixed(0)}%)`
                        }
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {analytics.mealBreakdown.map((_entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Attendance by Rank
                </CardTitle>
                <CardDescription>Distribution by masonic rank</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={analytics.attendanceByRank} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="rank" type="category" width={80} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill={COLORS.navy} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChartIcon className="h-5 w-5" />
                  RSVP Status
                </CardTitle>
                <CardDescription>Confirmed, waitlist, declined</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={analytics.attendanceByStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={80}
                      dataKey="count"
                    >
                      {analytics.attendanceByStatus.map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 2: Dietary, Unit, Payment Method */}
          <div className="mb-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {analytics.dietaryBreakdown.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Dietary Requirements</CardTitle>
                  <CardDescription>Special dietary needs</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={analytics.dietaryBreakdown} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="value" fill={COLORS.gold} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {analytics.paymentMethodBreakdown.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Payment Methods</CardTitle>
                  <CardDescription>How guests paid</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={analytics.paymentMethodBreakdown}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${value}`}
                        outerRadius={80}
                        dataKey="value"
                      >
                        {analytics.paymentMethodBreakdown.map((_entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number, name: string, props: any) => [
                        `${props.payload.value} payments, ${formatCurrency(props.payload.amount)}`,
                        props.payload.name,
                      ]} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Ticket type breakdown (when applicable) */}
          {analytics.attendanceByTicketType.length > 0 && (
            <div className="mb-8">
              <Card>
                <CardHeader>
                  <CardTitle>Attendance by Ticket Type</CardTitle>
                  <CardDescription>Guests and revenue per ticket type</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics.attendanceByTicketType}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="ticketName" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `£${v}`} />
                      <Tooltip
                        formatter={(value: number, name: string, props: any) => {
                          if (name === 'count') return [value, 'Attendees'];
                          return [formatCurrency(value), 'Revenue'];
                        }}
                      />
                      <Bar yAxisId="left" dataKey="count" fill={COLORS.navy} name="Attendees" radius={[4, 4, 0, 0]} />
                      <Bar yAxisId="right" dataKey="revenue" fill={COLORS.green} name="Revenue" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Timeline charts */}
          <div className="mb-8 grid gap-6 md:grid-cols-2">
            {analytics.paymentTimeline.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PoundSterling className="h-5 w-5" />
                    Payment Timeline
                  </CardTitle>
                  <CardDescription>Payments received over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <ComposedChart data={analytics.paymentTimeline}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis yAxisId="left" tickFormatter={(v) => `£${v}`} />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip
                        formatter={(value: number, name: string) => [
                          name === 'amount' ? formatCurrency(value) : value,
                          name === 'amount' ? 'Amount' : 'Payments',
                        ]}
                      />
                      <Legend />
                      <Bar yAxisId="right" dataKey="count" fill={COLORS.blue} name="Payments" radius={[4, 4, 0, 0]} />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="amount"
                        stroke={COLORS.green}
                        strokeWidth={2}
                        name="Amount"
                        dot={{ r: 4 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {analytics.rsvpTimeline.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    RSVP Timeline
                  </CardTitle>
                  <CardDescription>RSVPs received over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={analytics.rsvpTimeline}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke={COLORS.purple}
                        strokeWidth={2}
                        name="RSVPs"
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>
        </DashboardContentContainer>
      </main>
    </div>
  );
}
